import * as React from "react";
import { css } from "@emotion/core";
import {
  isUploadedFile,
  ProcessedGeometry,
} from "components/Orders/state/types";
import {
  CompoundTag,
  Icon,
  Card,
  Button,
  ButtonGroup,
} from "@blueprintjs/core";
import { BaseGeometryUpload } from "./BaseGeometryUpload";
import { ConfirmRemoveDialog } from "./ConfirmRemoveDialog";
import { GeometryMachineContext } from "../../state/geometryMachine";
import { getGeometryDisplayName } from "./utils";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { LidarMachineContext } from "components/Orders/state/lidarMachine";
import { useGeometryDropzone } from "../../hooks";
import { useUploadMachineSelector } from "../../context/upload-machine";

function renderShapefileTags(upload: ProcessedGeometry) {
  if (upload.type !== "shp") return null;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        align-items: start;
        gap: 0.25rem;
        margin-left: 0.5rem;
      `}
    >
      <CompoundTag
        size="large"
        leftContent={
          <Icon
            icon={
              upload.files.shp instanceof File ||
              isUploadedFile(upload.files.shp)
                ? "tick-circle"
                : "cross-circle"
            }
          />
        }
        intent={
          upload.files.shp instanceof File || isUploadedFile(upload.files.shp)
            ? "success"
            : "danger"
        }
      >
        .shp
      </CompoundTag>
      <CompoundTag
        size="large"
        leftContent={
          <Icon
            icon={
              upload.files.prj instanceof File ||
              isUploadedFile(upload.files.prj)
                ? "tick-circle"
                : "cross-circle"
            }
          />
        }
        intent={
          upload.files.prj instanceof File || isUploadedFile(upload.files.prj)
            ? "success"
            : "danger"
        }
      >
        .prj
      </CompoundTag>
      <CompoundTag
        size="large"
        leftContent={
          <Icon
            icon={
              upload.files.shx instanceof File ||
              isUploadedFile(upload.files.shx)
                ? "tick-circle"
                : "warning-sign"
            }
          />
        }
        intent={
          upload.files.shx instanceof File || isUploadedFile(upload.files.shx)
            ? "success"
            : "warning"
        }
      >
        .shx
      </CompoundTag>
      <CompoundTag
        size="large"
        leftContent={
          <Icon
            icon={
              upload.files.dbf instanceof File ||
              isUploadedFile(upload.files.dbf)
                ? "tick-circle"
                : "warning-sign"
            }
          />
        }
        intent={
          upload.files.dbf instanceof File || isUploadedFile(upload.files.dbf)
            ? "success"
            : "warning"
        }
      >
        .dbf
      </CompoundTag>
    </div>
  );
}

function renderGeoJSONTags(upload: ProcessedGeometry) {
  if (upload.type !== "geojson") return null;

  const hasFile =
    upload.files.geojson instanceof File ||
    isUploadedFile(upload.files.geojson);

  return (
    <CompoundTag
      size="large"
      leftContent={<Icon icon={hasFile ? "tick-circle" : "cross-circle"} />}
      intent={hasFile ? "success" : "danger"}
    >
      .geojson
    </CompoundTag>
  );
}

function renderKMLTags(upload: ProcessedGeometry) {
  if (upload.type !== "kml" && upload.type !== "kmz") return null;

  const hasFile =
    upload.files.kml instanceof File ||
    isUploadedFile(upload.files.kml) ||
    upload.files.kmz instanceof File ||
    isUploadedFile(upload.files.kmz);

  return (
    <CompoundTag
      size="large"
      leftContent={<Icon icon={hasFile ? "tick-circle" : "cross-circle"} />}
      intent={hasFile ? "success" : "danger"}
    >
      .{upload.type}
    </CompoundTag>
  );
}

function renderCSVTags(upload: ProcessedGeometry) {
  if (upload.type !== "csv") return null;

  const hasFile =
    upload.files.csv instanceof File || isUploadedFile(upload.files.csv);

  return (
    <CompoundTag
      size="large"
      leftContent={<Icon icon={hasFile ? "tick-circle" : "cross-circle"} />}
      intent={hasFile ? "success" : "danger"}
    >
      .csv
    </CompoundTag>
  );
}

function renderPDFTags(upload: ProcessedGeometry) {
  if (upload.type !== "pdf") return null;

  const hasFile =
    upload.files.pdf instanceof File || isUploadedFile(upload.files.pdf);

  return (
    <CompoundTag
      size="large"
      leftContent={<Icon icon={hasFile ? "tick-circle" : "cross-circle"} />}
      intent={hasFile ? "success" : "danger"}
    >
      .pdf
    </CompoundTag>
  );
}

function renderXLSXTags(upload: ProcessedGeometry) {
  if (upload.type !== "xlsx") return null;

  const hasFile =
    upload.files.xlsx instanceof File || isUploadedFile(upload.files.xlsx);

  return (
    <CompoundTag
      size="large"
      leftContent={<Icon icon={hasFile ? "tick-circle" : "cross-circle"} />}
      intent={hasFile ? "success" : "danger"}
    >
      .xlsx
    </CompoundTag>
  );
}

function ShapefileUpload({
  upload,
  onClick,
  onLabelChange,
}: {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
}) {
  return (
    <BaseGeometryUpload
      upload={upload}
      onClick={onClick}
      onLabelChange={onLabelChange}
    >
      {renderShapefileTags}
    </BaseGeometryUpload>
  );
}

function GeoJSONUpload({
  upload,
  onClick,
  onLabelChange,
}: {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
}) {
  return (
    <BaseGeometryUpload
      upload={upload}
      onClick={onClick}
      onLabelChange={onLabelChange}
    >
      {renderGeoJSONTags}
    </BaseGeometryUpload>
  );
}

function KMLUpload({
  upload,
  onClick,
  onLabelChange,
}: {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
}) {
  return (
    <BaseGeometryUpload
      upload={upload}
      onClick={onClick}
      onLabelChange={onLabelChange}
    >
      {renderKMLTags}
    </BaseGeometryUpload>
  );
}

function CSVUpload({
  upload,
  onClick,
  onLabelChange,
}: {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
}) {
  return (
    <BaseGeometryUpload
      upload={upload}
      onClick={onClick}
      onLabelChange={onLabelChange}
      editable={false}
    >
      {renderCSVTags}
    </BaseGeometryUpload>
  );
}

function PDFUpload({
  upload,
  onClick,
  onLabelChange,
}: {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
}) {
  return (
    <BaseGeometryUpload
      upload={upload}
      onClick={onClick}
      onLabelChange={onLabelChange}
      editable={false}
    >
      {renderPDFTags}
    </BaseGeometryUpload>
  );
}

function XLSXUpload({
  upload,
  onClick,
  onLabelChange,
}: {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
}) {
  return (
    <BaseGeometryUpload
      upload={upload}
      onClick={onClick}
      onLabelChange={onLabelChange}
      editable={false}
    >
      {renderXLSXTags}
    </BaseGeometryUpload>
  );
}

function GeometryConfirmRemoveDialog() {
  const geometryMachine = GeometryMachineContext.useActorRef();

  const show = GeometryMachineContext.useSelector((state) =>
    state.matches("remove")
  );

  const geometry = GeometryMachineContext.useSelector((state) =>
    state.context.uploads.find((u) => u.id === state.context.removing)
  );

  const handleCancel = () => {
    geometryMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    geometryMachine.send({ type: "CONFIRM_REMOVE" });
  };

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>{geometry ? getGeometryDisplayName(geometry) : ""}</span>
      </Card>
      <Card>
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span>Source Files</span>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: center;
              gap: 0.25rem;
              flex-wrap: wrap;
            `}
          >
            {geometry?.type === "shp"
              ? renderShapefileTags(geometry)
              : geometry?.type === "geojson"
              ? renderGeoJSONTags(geometry)
              : geometry?.type === "kml" || geometry?.type === "kmz"
              ? renderKMLTags(geometry)
              : geometry?.type === "csv"
              ? renderCSVTags(geometry)
              : geometry?.type === "pdf"
              ? renderPDFTags(geometry)
              : geometry?.type === "xlsx"
              ? renderXLSXTags(geometry)
              : null}
          </div>
        </div>
      </Card>
    </ConfirmRemoveDialog>
  );
}

function GeometryConfirmRemoveMissingDialog() {
  const geometryMachine = GeometryMachineContext.useActorRef();

  const show = GeometryMachineContext.useSelector((state) =>
    state.matches({ removeMissing: "confirm" })
  );

  const missingCount = GeometryMachineContext.useSelector(
    (state) => state.context.uploads.filter((upload) => upload.missing).length
  );

  const handleCancel = () => {
    geometryMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    geometryMachine.send({ type: "CONFIRM_REMOVE_MISSING" });
  };

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>
          Remove {missingCount} missing file
          {missingCount === 1 ? "" : "s"}?
        </span>
      </Card>
    </ConfirmRemoveDialog>
  );
}

function ReaddMissingGeometryButton() {
  const geometryMachine = GeometryMachineContext.useActorRef();
  const geometryDropzone = useGeometryDropzone(geometryMachine);

  return (
    <Button
      icon="shapes"
      intent="success"
      size="small"
      {...geometryDropzone.getRootProps()}
    >
      <input {...geometryDropzone.getInputProps()} />
      Re-add Missing Files
    </Button>
  );
}

function RemoveMissingGeometryButton() {
  const geometryMachine = GeometryMachineContext.useActorRef();

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const handleClick = () => {
    geometryMachine.send({ type: "REMOVE_MISSING" });
  };

  return (
    <Button
      icon="trash"
      intent="danger"
      size="small"
      disabled={uploadInProgress}
      onClick={handleClick}
    >
      Remove Missing Files
    </Button>
  );
}

interface GeometryListProps {
  geometry: ProcessedGeometry[];
  onFocus?: (bbox: number[]) => void;
}

export default function GeometryList({ geometry, onFocus }: GeometryListProps) {
  const geometryMachine = GeometryMachineContext.useActorRef();

  const imagesUploads = ImagesMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const lidarUploads = LidarMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const warningGeometryOnly =
    geometryUploads.length > 0 &&
    imagesUploads.length === 0 &&
    lidarUploads.length === 0;

  const warningIncompleteShapefile =
    geometryUploads.filter(
      (upload) =>
        upload.type === "shp" &&
        Object.values(upload.files).every(
          (file) => file instanceof File || isUploadedFile(file)
        ) === false
    ).length > 0;

  const missingGeometry = geometryUploads.filter((upload) => upload.missing);

  const errorMissingGeometry = missingGeometry.length > 0;

  const handleLabelChange = (id: string, label: string) => {
    geometryMachine.send({
      type: "UPDATE_LABEL",
      id,
      label: label.trim() || undefined,
    });
  };

  return (
    <div
      css={css`
        flex-grow: 1;
        width: 100%;
        border-top: 1px solid var(--neutral-5);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          background-color: var(--neutral-6);
          border-bottom: 1px solid var(--neutral-5);
          padding: 0.5rem 1rem 0.5rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <h4
          css={css`
            color: var(--neutral-3);
            margin: 0;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          <span>
            {geometry.length === 1
              ? "1 Layer/Accessory"
              : `${geometry.length} Layers/Accessories`}
          </span>
          {errorMissingGeometry ? (
            <span
              css={css`
                color: var(--danger);
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.25rem;
              `}
            >
              <Icon icon="error" color="var(--danger)" />
              {missingGeometry.length} Missing
            </span>
          ) : null}
        </h4>
        {errorMissingGeometry ? (
          <div
            css={css`
              flex-direction: row;
              align-items: center;
              gap: 0.5rem;
              display: flex;
            `}
          >
            <ButtonGroup>
              <ReaddMissingGeometryButton />
              <RemoveMissingGeometryButton />
            </ButtonGroup>
          </div>
        ) : warningGeometryOnly ? (
          <div
            css={css`
              flex-direction: row;
              align-items: center;
              gap: 0.5rem;
              display: flex;
            `}
          >
            <Icon icon="error" color="var(--danger)" />
            <span
              css={css`
                color: var(--danger);
              `}
            >
              Cannot submit order without images or point clouds
            </span>
          </div>
        ) : warningIncompleteShapefile ? (
          <div
            css={css`
              flex-direction: row;
              align-items: center;
              gap: 0.5rem;
              display: flex;
            `}
          >
            <Icon icon="warning-sign" color="var(--warning)" />
            <span
              css={css`
                color: var(--warning);
              `}
            >
              A shapefile is missing one more more components.
            </span>
          </div>
        ) : null}
      </div>
      <div
        css={css`
          flex-grow: 1;
          padding: 1rem;
          overflow: auto;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        {geometry.map((upload) => {
          const handleClick = () => {
            if (onFocus && upload.data?.bbox) {
              onFocus(upload.data.bbox);
            }
          };

          return upload.type === "shp" ? (
            <ShapefileUpload
              key={upload.id}
              upload={upload}
              onClick={handleClick}
              onLabelChange={handleLabelChange}
            />
          ) : upload.type === "geojson" ? (
            <GeoJSONUpload
              key={upload.id}
              upload={upload}
              onClick={handleClick}
              onLabelChange={handleLabelChange}
            />
          ) : upload.type === "kml" || upload.type === "kmz" ? (
            <KMLUpload
              key={upload.id}
              upload={upload}
              onClick={handleClick}
              onLabelChange={handleLabelChange}
            />
          ) : upload.type === "csv" ? (
            <CSVUpload
              key={upload.id}
              upload={upload}
              onClick={handleClick}
              onLabelChange={handleLabelChange}
            />
          ) : upload.type === "pdf" ? (
            <PDFUpload
              key={upload.id}
              upload={upload}
              onClick={handleClick}
              onLabelChange={handleLabelChange}
            />
          ) : upload.type === "xlsx" ? (
            <XLSXUpload
              key={upload.id}
              upload={upload}
              onClick={handleClick}
              onLabelChange={handleLabelChange}
            />
          ) : null;
        })}
      </div>
      <GeometryConfirmRemoveDialog />
      <GeometryConfirmRemoveMissingDialog />
    </div>
  );
}
